import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../../services/api';
import { formatSlideContent } from '../utils/formatSlideContent';
import { extractFilenameFromHeaders } from '../../rfp/shared/utils/documentProgress';
import { arrayMove } from '@dnd-kit/sortable';

const parseMarkdownToStructuredContent = (markdownText, layout) => {
    if (!markdownText || typeof markdownText !== 'string') return [];

    const lines = markdownText.split(/\n+/);
    const items = [];

    for (let line of lines) {
        line = line.trim();
        if (!line) continue;

        // Strip only actual list markers followed by spaces (e.g. "- ", "* ", "• ", "1. ")
        // This avoids accidentally stripping bold asterisks "**" at the start of a line!
        line = line.replace(/^([-\*•]|\d+\.)\s+/, '').trim();
        if (!line) continue;

        const boldMatch = line.match(/^\*\*(.*?)\*\*[:\s-]*(.*)$/);

        let headline = "";
        let subtext = "";

        if (boldMatch) {
            headline = boldMatch[1].trim();
            subtext = boldMatch[2].trim();
        } else {
            const colonIdx = line.indexOf(':');
            if (colonIdx > 0) {
                headline = line.substring(0, colonIdx).trim();
                subtext = line.substring(colonIdx + 1).trim();
            } else {
                headline = line;
                subtext = "";
            }
        }

        // Strip any remaining markdown bold markers from final headline/subtext strings
        headline = headline.replace(/\*\*/g, '').trim();
        subtext = subtext.replace(/\*\*/g, '').trim();

        const lowerLayout = (layout || '').toLowerCase();

        if (lowerLayout.includes('feature')) {
            items.push({ Feature: headline, Description: subtext });
        } else if (lowerLayout.includes('mandate')) {
            items.push({ Mandate: headline, Description: subtext });
        } else if (lowerLayout.includes('component')) {
            items.push({ Component: headline, Description: subtext });
        } else {
            items.push({ headline, subtext });
        }
    }

    return items;
};

export const usePPTCustomization = (rfpId, location, showToast = () => {}) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const [expandedSections, setExpandedSections] = useState(['1', '2', '3']);
    const [sections, setSections] = useState([]);
    const [activeSectionId, setActiveSectionId] = useState(null);
    const [activeTitleId, setActiveTitleId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [generatingSlideId, setGeneratingSlideId] = useState(null);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [isCreatingNewSection, setIsCreatingNewSection] = useState(false);
    const [newSectionForm, setNewSectionForm] = useState({ section_name: '', slide_title: '', system_prompt: '', content_type: 'bullets' });
    const [isCreatingNewSlide, setIsCreatingNewSlide] = useState(false);
    const [creatingInSectionId, setCreatingInSectionId] = useState(null);
    const [newSlideForm, setNewSlideForm] = useState({ title: '', system_prompt: '', content_type: 'bullets' });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [targetToDelete, setTargetToDelete] = useState(null);
    const [activeDragId, setActiveDragId] = useState(null);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [selectedLogo, setSelectedLogo] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [originalSlides, setOriginalSlides] = useState({});
    const [generationSuccessMessage, setGenerationSuccessMessage] = useState(null);
    const [draftCount, setDraftCount] = useState(0);

    const activeSection = sections.find(s => s.id === activeSectionId);
    const activeTitle = activeSection?.titles.find(t => t.id === activeTitleId);

    const fetchPPTConfig = useCallback(async (forceApi = false) => {
        if (!rfpId) return;
        setIsLoading(true);
        try {
            const generationResponse = location.state?.generationResponse;
            if (!forceApi && generationResponse && generationResponse.sections && sections.length === 0) {
                const transformed = generationResponse.sections.map(item => ({
                    id: item.section_id || `sec-${Math.random()}`,
                    name: (item.title || item.section_name || 'Untitled Section').toUpperCase(),
                    type: 'dynamic',
                    titles: (item.slides || []).map(slide => {
                        const contentStr = formatSlideContent(slide.content, slide.column_headers);
                        const isStatic = slide.type === 'static' || slide.is_static === true;
                        return {
                            id: slide.slide_id || slide.key || `slide-${Math.random()}`,
                            key: slide.key, text: slide.title || 'Untitled Slide',
                            content: contentStr || formatSlideContent(slide.ai_analysis, slide.column_headers) || "",
                            rawContent: slide.content,
                            archetype: slide.archetype || slide.content_type || 'bullets',
                            type: isStatic ? 'static' : (slide.type || 'ai'),
                            layout: slide.archetype || slide.content_type || 'bullet',
                            is_static: isStatic,
                            isEditable: !isStatic,
                            prompt: slide.system_prompt || "",
                            previewUrl: slide.preview_url || null,
                            overflowPreviews: slide.overflow_previews || [],
                            column_headers: slide.column_headers || null,
                            slide_id: slide.slide_id || slide.id || slide.key || ""
                        };
                    })
                }));
                setSections(transformed);
                const slidesMap = {};
                transformed.forEach(sec => {
                    (sec.titles || []).forEach(slide => {
                        slidesMap[slide.id] = { content: slide.content, text: slide.text, layout: slide.layout };
                    });
                });
                setOriginalSlides(slidesMap);
                if (transformed.length > 0) {
                    const firstSec = transformed[0];
                    setActiveSectionId(firstSec.id);
                    if (firstSec.titles?.length > 0) setActiveTitleId(firstSec.titles[0].id);
                    setExpandedSections([firstSec.id]);
                }
                setIsLoading(false);
                return;
            }

            const response = await api.get(`/ppt-config/rfp/${rfpId}/all`);
            let rawData = response.data?.sections || response.data?.slide_order?.sections || response.data?.slide_order || (Array.isArray(response.data) ? response.data : []);

            if (rawData && Array.isArray(rawData)) {
                const transformed = rawData.map((item) => ({
                    id: item.section_id || item.id || item._id || `sec-${Math.random()}`,
                    name: (item.title || item.section_name || 'Untitled Section').toUpperCase(),
                    type: item.type,
                    titles: (item.slides || []).map((slide) => {
                        const contentStr = formatSlideContent(slide.content, slide.column_headers);
                        const isStatic = slide.type === 'static' || slide.is_static === true;
                        return {
                            id: slide.slide_id || slide.id || slide.key || `slide-${Math.random()}`,
                            key: slide.key || slide.slide_id || slide.id || "",
                            text: slide.title || 'Untitled Slide',
                            content: contentStr || formatSlideContent(slide.ai_analysis, slide.column_headers) || "",
                            rawContent: slide.content,
                            archetype: slide.archetype || slide.content_type || slide.layout || 'bullets',
                            type: isStatic ? 'static' : (slide.type || 'ai'),
                            layout: slide.archetype || slide.content_type || slide.layout || 'bullet',
                            is_static: isStatic,
                            isEditable: !isStatic,
                            prompt: slide.system_prompt || slide.prompt || "",
                            previewUrl: slide.preview_url || null,
                            overflowPreviews: slide.overflow_previews || [],
                            column_headers: slide.column_headers || null,
                            slide_id: slide.slide_id || slide.id || slide.key || ""
                        };
                    })
                }));
                setSections(transformed);
                const slidesMap = {};
                transformed.forEach(sec => {
                    (sec.titles || []).forEach(slide => {
                        slidesMap[slide.id] = { content: slide.content, text: slide.text, layout: slide.layout };
                    });
                });
                setOriginalSlides(slidesMap);
                if (!forceApi && transformed.length > 0 && !activeSectionId) {
                    const firstSection = transformed[0];
                    setActiveSectionId(firstSection.id);
                    if (firstSection.titles?.length > 0) setActiveTitleId(firstSection.titles[0].id);
                    setExpandedSections([firstSection.id]);
                }
                return transformed;
            }
        } catch (error) { console.error('Error fetching PPT config:', error); }
        finally { setIsLoading(false); }
    }, [rfpId, location.state, sections.length]);

    useEffect(() => { fetchPPTConfig(); }, [fetchPPTConfig]);

    const handlePreviewSlide = useCallback((sectionId, titleId, isUserTriggered = false) => {
        const slide = sections.find(s => s.id === sectionId)?.titles.find(t => t.id === titleId);
        if (slide?.previewUrl) {
            setPreviewImageUrl(slide.previewUrl);
        } else {
            setPreviewImageUrl(null);
            if (isUserTriggered) showToast('No preview available for this slide', 'info');
        }
    }, [sections, showToast]);

    const lastViewedSlideId = useRef(null);

    useEffect(() => {
        // Prevent keystrokes (which mutate sections) from resetting the UI to preview mode
        if (activeTitleId && activeTitleId !== lastViewedSlideId.current) {
            lastViewedSlideId.current = activeTitleId;
            setPreviewImageUrl(null);
            if (activeSectionId) {
                handlePreviewSlide(activeSectionId, activeTitleId);
            }
        }
    }, [activeTitleId, activeSectionId, handlePreviewSlide]);

    const toggleSection = (id) => setExpandedSections(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
    const selectTitle = (secId, titleId) => { setActiveSectionId(secId); setActiveTitleId(titleId); };

    const handleAddSection = () => {
        setIsCreatingNewSection(true); setActiveSectionId(null); setActiveTitleId(null);
        setNewSectionForm({ section_name: '', slide_title: '', system_prompt: '', content_type: 'bullet' });
    };

    const handleSaveNewSection = async () => {
        const sectionName = newSectionForm.section_name.trim() || 'New Section';
        const finalPayload = { title: sectionName, section_name: sectionName.toLowerCase().replace(/\s+/g, '_'), target_index: sections.length, slides: [{ title: newSectionForm.slide_title?.trim() || 'Overview', system_prompt: newSectionForm.system_prompt?.trim() || '', content_type: newSectionForm.content_type || 'bullet' }] };
        setIsSaving(true);
        try {
            const response = await api.post(`/ppt-config/rfp/${rfpId}/sections`, finalPayload);
            if (response.data) {
                showToast(response.data.message || 'Section created successfully!', 'success');
                setDraftCount(prev => prev + 1);
                const refreshedData = await fetchPPTConfig(true);
                if (refreshedData) {
                    const found = refreshedData.find(s => s.name.toUpperCase() === sectionName.toUpperCase());
                    if (found) { setActiveSectionId(found.id); if (found.titles?.length > 0) setActiveTitleId(found.titles[0].id); setExpandedSections(prev => [...new Set([...prev, found.id])]); }
                }
                setIsCreatingNewSection(false);
            }
        } catch (error) { showToast(`API Error: ${error.response?.data?.message || error.message}`, 'error'); }
        finally { setIsSaving(false); }
    };

    const handleAddTitle = (sectionId) => {
        setIsCreatingNewSlide(true); setIsCreatingNewSection(false); setCreatingInSectionId(sectionId);
        setActiveSectionId(sectionId); setActiveTitleId(null); setNewSlideForm({ title: '', system_prompt: '', content_type: 'bullet' });
    };

    const handleConfirmCreateSlide = async () => {
        if (!newSlideForm.title.trim()) { showToast('Please enter a slide title', 'warning'); return; }
        const payload = { title: newSlideForm.title, system_prompt: newSlideForm.system_prompt, content_type: newSlideForm.content_type };
        setIsSaving(true);
        try {
            const response = await api.post(`/ppt-config/rfp/${rfpId}/sections/${creatingInSectionId}/slides`, payload, { headers: { 'rfp_id': rfpId, 'section_id': creatingInSectionId } });
            if (response.data) {
                showToast(response.data.message || 'Slide created successfully!', 'success');
                setDraftCount(prev => prev + 1);
                const refreshedData = await fetchPPTConfig(true);
                if (refreshedData) {
                    const updatedSection = refreshedData.find(s => s.id === creatingInSectionId);
                    if (updatedSection) {
                        const newSlide = updatedSection.titles.find(t => t.id === response.data.slide_id) || updatedSection.titles[updatedSection.titles.length - 1];
                        setActiveSectionId(updatedSection.id); setActiveTitleId(newSlide.id);
                        setExpandedSections(prev => [...new Set([...prev, updatedSection.id])]);
                    }
                }
                setIsCreatingNewSlide(false);
            }
        } catch (error) { showToast(`Error creating slide: ${error.response?.data?.message || error.message}`, 'error'); }
        finally { setIsSaving(false); }
    };

    const handleUpdateSlide = async (sectionId, titleId) => {
        const slide = sections.find(s => s.id === sectionId)?.titles.find(t => t.id === titleId);
        if (!slide) return;

        const original = originalSlides[titleId];
        const isContentChanged = !original || slide.content !== original.content;
        const isTitleChanged = !original || slide.text !== original.text;
        const isLayoutChanged = !original || slide.layout !== original.layout;

        if (!isContentChanged && !isTitleChanged && !isLayoutChanged) return;

        const realSlideId = slide.slide_id || titleId;
        setIsSaving(true);
        try {
            const headers = { 'rfp_id': rfpId, 'section_id': sectionId, 'slide_id': titleId };

            let lastResponse = null;
            if (isContentChanged || isLayoutChanged) {
                const structuredContent = parseMarkdownToStructuredContent(slide.content, slide.layout);
                lastResponse = await api.patch(`/ppt-config/rfp/${rfpId}/sections/${sectionId}/slides/${realSlideId}`, { content: structuredContent }, { headers });
            }
            if (isTitleChanged) {
                lastResponse = await api.patch(`/ppt-config/rfp/${rfpId}/sections/${sectionId}/slides/${realSlideId}/title`, { title: slide.text }, { headers });
            }
            if (lastResponse?.data) {
                showToast(lastResponse.data.message || 'Slide saved as draft!', 'success');
                setDraftCount(prev => prev + 1);
            }
            await fetchPPTConfig(true);
        } catch (error) { showToast(`Error updating slide: ${error.response?.data?.message || error.message}`, 'error'); }
        finally { setIsSaving(false); }
    };

    // Called by SlideContentEditor with pre-structured content (bypasses markdown parser)
    const handleSaveSlideContent = async (sectionId, titleId, structuredContent) => {
        const slide = sections.find(s => s.id === sectionId)?.titles.find(t => t.id === titleId);
        if (!slide) return;
        const realSlideId = slide.slide_id || titleId;
        setIsSaving(true);
        try {
            const headers = { 'rfp_id': rfpId, 'section_id': sectionId, 'slide_id': titleId };
            const response = await api.patch(`/ppt-config/rfp/${rfpId}/sections/${sectionId}/slides/${realSlideId}`, { content: structuredContent }, { headers });
            if (response.data) {
                showToast(response.data.message || 'Slide content saved as draft!', 'success');
                setDraftCount(prev => prev + 1);
            }
            await fetchPPTConfig(true);
        } catch (error) { showToast(`Error saving slide: ${error.response?.data?.message || error.message}`, 'error'); }
        finally { setIsSaving(false); }
    };
    const handleUpdateSection = async (sectionId, newTitle) => {
        if (!newTitle.trim()) return;
        try {
            const response = await api.patch(`/ppt-config/rfp/${rfpId}/sections/${sectionId}`, { title: newTitle, section_name: newTitle }, { headers: { 'rfp_id': rfpId, 'section_id': sectionId } });
            if (response.data) {
                showToast(response.data.message || 'Section title updated successfully', 'success');
                setDraftCount(prev => prev + 1);
            }
            await fetchPPTConfig(true);
        } catch (error) { showToast(`Error updating section: ${error.response?.data?.message || error.message}`, 'error'); }
    };

    const handleDownloadPPT = async (lang = 'en', themeId = null) => {
        setIsDownloading(true);
        try {
            const lowerLang = String(lang).toLowerCase();
            const savedTemplateId = location?.state?.generationResponse?.template_id || location?.state?.analysisData?.template_id;
            const effectiveThemeId = themeId || savedTemplateId;

            let body = null; 
            let headers = { 
                'rfp_id': rfpId,
                'Accept-Language': lowerLang,
                'language': lowerLang
            };
            if (selectedLogo) { const formData = new FormData(); formData.append('logo', selectedLogo); body = formData; } else { body = {}; }
            
            let url = `/rfp/report/${rfpId}?format=pptx&language=${lowerLang}`;
            if (effectiveThemeId) {
                url += `&template_id=${effectiveThemeId}`;
            }
            
            const response = await api.post(url, body, { headers, responseType: 'blob' });
            if (response.status !== 200) throw new Error('Failed to download PPT.');
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a'); link.href = downloadUrl; link.setAttribute('download', extractFilenameFromHeaders(response, `RFP_Presentation_${rfpId}.pptx`));
            document.body.appendChild(link); link.click(); document.body.removeChild(link); window.URL.revokeObjectURL(downloadUrl);
            setShowGenerateModal(false); setSelectedLogo(null);
        } catch (error) {
            console.error('Download PPT error:', error);
            let errorMsg = `Download failed: ${error.message}`;
            if (error.response?.data) {
                try {
                    let errorData = error.response.data;
                    if (errorData instanceof Blob) {
                        const text = await errorData.text();
                        try {
                            errorData = JSON.parse(text);
                        } catch (e) {
                            if (text) {
                                errorMsg = text;
                            }
                        }
                    }
                    if (errorData?.detail) {
                        errorMsg = errorData.detail;
                    } else if (errorData?.message) {
                        errorMsg = errorData.message;
                    }
                } catch (e) {
                    console.error('Error decoding error response blob:', e);
                }
            }
            showToast(errorMsg, 'error');
        }
        finally { setIsDownloading(false); }
    };

    const handleDeleteSection = (sectionId) => {
        const sectionObj = sections.find(s => s.id === sectionId);
        setTargetToDelete({ sectionId, title: sectionObj?.name || 'this section', type: 'section' }); setShowDeleteModal(true);
    };

    const handleDeleteTitle = (sectionId, titleId) => {
        const titleObj = sections.find(s => s.id === sectionId)?.titles.find(t => t.id === titleId);
        setTargetToDelete({ sectionId, titleId, title: titleObj?.text || 'this slide', type: 'slide' }); setShowDeleteModal(true);
    };

    const confirmDeleteAction = async () => {
        if (!targetToDelete) return false;
        const { sectionId, titleId, type } = targetToDelete;
        try {
            let response = null;
            if (type === 'section') {
                response = await api.delete(`/ppt-config/rfp/${rfpId}/sections/${sectionId}`, { data: { rfp_id: rfpId, section_id: sectionId } });
                setSections(sections.filter(sec => sec.id !== sectionId));
                if (activeSectionId === sectionId) { setActiveSectionId(null); setActiveTitleId(null); }
            } else {
                response = await api.delete(`/ppt-config/rfp/${rfpId}/sections/${sectionId}/slides/${titleId}`, { headers: { 'rfp_id': rfpId, 'section_id': sectionId, 'slide_id': titleId } });
                setSections(sections.map(sec => sec.id === sectionId ? { ...sec, titles: sec.titles.filter(t => t.id !== titleId) } : sec));
                if (activeTitleId === titleId) setActiveTitleId(null);
            }
            if (response?.data) {
                showToast(response.data.message || `${type === 'section' ? 'Section' : 'Slide'} deleted successfully!`, 'success');
            }
            setShowDeleteModal(false); setTargetToDelete(null); return true;
        } catch (error) { showToast(`Error deleting item: ${error.response?.data?.message || error.message}`, 'error'); return false; }
    };

    const handleGenerateSlide = async (sectionId, titleId) => {
        const titleObj = sections.find(s => s.id === sectionId)?.titles.find(t => t.id === titleId);
        if (!titleObj) return;
        setGeneratingSlideId(titleId);
        try {
            const realSlideId = titleObj.slide_id || titleId;
            const payload = {
                archetype: titleObj.layout || "auto",
                system_prompt: titleObj.prompt || "",
            };
            const response = await api.post(`/ppt-config/rfp/${rfpId}/sections/${sectionId}/slides/${realSlideId}/regenerate`, payload, {
                headers: { 'rfp_id': rfpId, 'section_id': sectionId, 'slide_id': realSlideId }
            });
            if (response.data) {
                setGenerationSuccessMessage(response.data.message || 'Slide content generated successfully!');
                const rawData = response.data.slide_order?.sections || response.data.slide_order || response.data.sections;
                if (rawData && Array.isArray(rawData)) {
                    const transformed = rawData.map((item) => ({
                        id: item.section_id || item.id || item._id || `sec-${Math.random()}`,
                        name: (item.title || item.section_name || 'Untitled Section').toUpperCase(),
                        type: item.type,
                        titles: (item.slides || []).map((slide) => {
                            const contentStr = formatSlideContent(slide.content, slide.column_headers);
                            const isStatic = slide.type === 'static' || slide.is_static === true;
                            return {
                                id: slide.slide_id || slide.id || slide.key || `slide-${Math.random()}`,
                                key: slide.key || slide.slide_id || slide.id || "",
                                text: slide.title || 'Untitled Slide',
                                content: contentStr || formatSlideContent(slide.ai_analysis, slide.column_headers) || "",
                                rawContent: slide.content,
                                archetype: slide.archetype || slide.content_type || slide.layout || 'bullets',
                                type: isStatic ? 'static' : (slide.type || 'ai'),
                                layout: slide.archetype || slide.content_type || slide.layout || 'bullet',
                                is_static: isStatic,
                                isEditable: !isStatic,
                                prompt: slide.system_prompt || slide.prompt || "",
                                previewUrl: slide.preview_url || null,
                                overflowPreviews: slide.overflow_previews || [],
                                column_headers: slide.column_headers || null,
                                slide_id: slide.slide_id || slide.id || slide.key || ""
                            };
                        })
                    }));
                    setSections(transformed);
                    const slidesMap = {};
                    transformed.forEach(sec => {
                        (sec.titles || []).forEach(slide => {
                            slidesMap[slide.id] = { content: slide.content, text: slide.text, layout: slide.layout };
                        });
                    });
                    setOriginalSlides(slidesMap);
                    
                    // Trigger a preview refresh for the regenerated slide
                    const updatedSec = transformed.find(s => s.id === sectionId);
                    const updatedTitle = updatedSec?.titles.find(t => t.id === titleId);
                    if (updatedTitle?.previewUrl) {
                        setPreviewImageUrl(updatedTitle.previewUrl);
                    }
                } else {
                    setSections(sections.map(sec => sec.id === sectionId ? { ...sec, titles: sec.titles.map(t => t.id === titleId ? { 
                        ...t, 
                        content: formatSlideContent(response.data.content) || response.data.content || t.content, 
                        previewUrl: response.data.preview_url || t.previewUrl, 
                        overflowPreviews: response.data.overflow_previews || t.overflowPreviews, 
                        archetype: response.data.archetype || response.data.content_type || t.archetype,
                        layout: response.data.archetype || response.data.content_type || t.layout,
                        isGenerated: true 
                    } : t) } : sec));
                }
            }
        } catch (error) { 
            showToast(`Error generating slide: ${error.response?.data?.message || error.message}`, 'error'); 
        } finally { 
            setGeneratingSlideId(null); 
        }
    };

    const handleRefreshSlide = async (sectionId, titleId) => {
        setGeneratingSlideId(titleId);
        try {
            const refreshedData = await fetchPPTConfig(true);
            if (refreshedData) {
                const updatedSec = refreshedData.find(s => s.id === sectionId);
                const updatedTitle = updatedSec?.titles.find(t => t.id === titleId);
                if (updatedTitle?.previewUrl) {
                    setPreviewImageUrl(updatedTitle.previewUrl);
                }
            }
        } catch (error) {
            showToast(`Error refreshing slide: ${error.response?.data?.message || error.message}`, 'error');
        } finally {
            setGeneratingSlideId(null);
        }
    };

    const handleFieldChange = (sectionId, titleId, field, value) => {
        setSections(sections.map(sec => sec.id === sectionId ? { ...sec, titles: sec.titles.map(t => t.id === titleId ? { ...t, [field]: value } : t) } : sec));
    };

    const handleDragStart = (event) => setActiveDragId(event.active.id);

    const handleDragOver = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        let activeContainer = null, overContainer = null;
        sections.forEach(sec => {
            if (sec.titles.find(t => t.id === active.id)) activeContainer = sec.id;
            if (sec.titles.find(t => t.id === over.id)) overContainer = sec.id;
            if (sec.id === over.id) overContainer = sec.id;
        });
        if (!activeContainer || !overContainer || activeContainer === overContainer) return;
        setSections(prev => {
            const activeSec = prev.find(s => s.id === activeContainer), overSec = prev.find(s => s.id === overContainer);
            if (!activeSec || !overSec) return prev;
            const activeIndex = activeSec.titles.findIndex(t => t.id === active.id);
            const overIndex = overSec.titles.findIndex(t => t.id === over.id);
            const newIndex = overIndex >= 0 ? overIndex : overSec.titles.length;
            const activeSlide = activeSec.titles[activeIndex];
            return prev.map(sec => {
                if (sec.id === activeContainer) return { ...sec, titles: sec.titles.filter(t => t.id !== active.id) };
                if (sec.id === overContainer) { const newTitles = [...sec.titles]; newTitles.splice(newIndex, 0, activeSlide); return { ...sec, titles: newTitles }; }
                return sec;
            });
        });
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        setActiveDragId(null);
        if (!over) return;
        let targetSection = null, finalIndex = -1;
        sections.forEach(sec => { const idx = sec.titles.findIndex(t => t.id === active.id); if (idx !== -1) { targetSection = sec; finalIndex = idx; } });
        if (!targetSection) return;
        if (active.id !== over.id) {
            const activeSId = sections.find(s => s.titles.find(t => t.id === active.id))?.id;
            const overSId = sections.find(s => s.titles.find(t => t.id === over.id))?.id;
            if (activeSId === overSId) {
                const oldIdx = targetSection.titles.findIndex(t => t.id === active.id);
                const newIdx = targetSection.titles.findIndex(t => t.id === over.id);
                if (oldIdx !== newIdx) {
                    setSections(prev => prev.map(sec => sec.id === targetSection.id ? { ...sec, titles: arrayMove(sec.titles, oldIdx, newIdx) } : sec));
                    finalIndex = newIdx;
                }
            }
        }
        const prevSections = [...sections];
        try {
            await api.patch(`/ppt-config/rfp/${rfpId}/slide-order/move`, { id: active.id, target_index: finalIndex });
            await fetchPPTConfig(true);
        } catch (error) { setSections(prevSections); showToast('Failed to reorder slide. Rolling back...', 'error'); }
    };

    return {
        isEditMode, setIsEditMode, expandedSections, setExpandedSections, sections, setSections,
        activeSectionId, setActiveSectionId, activeTitleId, setActiveTitleId, isLoading,
        generatingSlideId, setGeneratingSlideId, isSidebarCollapsed, setIsSidebarCollapsed,
        showPreview, setShowPreview, isCreatingNewSection, setIsCreatingNewSection,
        newSectionForm, setNewSectionForm, isCreatingNewSlide, setIsCreatingNewSlide,
        creatingInSectionId, setCreatingInSectionId, newSlideForm, setNewSlideForm,
        showDeleteModal, setShowDeleteModal, targetToDelete, setTargetToDelete,
        activeDragId, setActiveDragId, showGenerateModal, setShowGenerateModal,
        selectedLogo, setSelectedLogo, isDownloading, setIsDownloading,
        isSaving, setIsSaving, previewImageUrl, setPreviewImageUrl,
        isPreviewLoading, setIsPreviewLoading, originalSlides, setOriginalSlides,
        generationSuccessMessage, setGenerationSuccessMessage,
        activeSection, activeTitle, toggleSection, selectTitle, handlePreviewSlide,
        handleAddSection, handleSaveNewSection, handleAddTitle, handleConfirmCreateSlide,
        handleUpdateSlide, handleUpdateSection, handleDownloadPPT, handleDeleteSection,
        handleDeleteTitle, confirmDeleteAction, handleGenerateSlide, handleRefreshSlide, handleFieldChange,
        handleSaveSlideContent,
        handleDragStart, handleDragOver, handleDragEnd,
        draftCount, setDraftCount
    };
};
